// AnglesPage.jsx
// Save in: src/quora/AnglesPage.jsx
//
// Manage Angles & CTAs — view, add, edit and delete Angles (each Angle holds
// Keywords, CTAs, Basic Info and Brand Voice used during AI answer generation).
// Ticket: Sean Frances asked for a way to view/edit/delete existing CTAs and Angles
// instead of only being able to add new ones from the "Add Angles" modal.

import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Tag, Space, Typography, Tooltip, Popconfirm, Empty,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Target } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../components/Axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import AngleForm from '../components/AngleForm';

const { Text, Title } = Typography;

const AnglesPage = () => {
  const storedUser = localStorage.getItem('user');
  const userId = storedUser ? JSON.parse(storedUser)._id : null;

  const [angles, setAngles]   = useState([]);
  const [loading, setLoading] = useState(false);

  const [formVisible, setFormVisible]     = useState(false);
  const [editingAngle, setEditingAngle]   = useState(null);

  // ── Load angles ────────────────────────────────────────────────────────────
  const loadAngles = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/user-angles/${userId}`);
      if (res.data.success) setAngles(res.data.data);
    } catch {
      toast.error('Failed to load angles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAngles(); }, [userId]);

  // ── Open create / edit ────────────────────────────────────────────────────
  const openCreate = () => { setEditingAngle(null); setFormVisible(true); };
  const openEdit    = (angle) => { setEditingAngle(angle); setFormVisible(true); };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (angleId) => {
    try {
      const res = await axios.delete(`/api/angle/${angleId}`);
      if (res.data.success) {
        toast.success('Angle deleted!');
        setAngles(prev => prev.filter(a => a._id !== angleId));
      }
    } catch {
      toast.error('Failed to delete angle');
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Angle Name',
      dataIndex: 'angleName',
      key: 'angleName',
      render: (text, record) => (
        <div>
          <Text strong style={{ fontSize: '13px' }}>{text}</Text>
          {record.basicInfo && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {record.basicInfo.length > 70 ? record.basicInfo.substring(0, 70) + '…' : record.basicInfo}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Keywords',
      dataIndex: 'keywords',
      key: 'keywords',
      render: (keywords = []) => (
        keywords.length ? (
          <Space size={[4, 4]} wrap>
            {keywords.slice(0, 3).map((k, i) => (
              <Tag key={i} color="green" style={{ fontSize: '11px' }}>{k}</Tag>
            ))}
            {keywords.length > 3 && (
              <Tag style={{ fontSize: '11px' }}>+{keywords.length - 3} more</Tag>
            )}
          </Space>
        ) : <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      ),
    },
    {
      title: 'CTAs',
      dataIndex: 'ctas',
      key: 'ctas',
      render: (ctas = []) => (
        ctas.length ? (
          <Space size={[4, 4]} wrap>
            {ctas.slice(0, 2).map((c, i) => (
              <Tag key={i} color="orange" style={{ fontSize: '11px' }}>
                {c.length > 24 ? c.substring(0, 24) + '…' : c}
              </Tag>
            ))}
            {ctas.length > 2 && (
              <Tag style={{ fontSize: '11px' }}>+{ctas.length - 2} more</Tag>
            )}
          </Space>
        ) : <Text type="secondary" style={{ fontSize: '12px' }}>—</Text>
      ),
    },
    {
      title: 'Brand Voice',
      dataIndex: 'brandVoice',
      key: 'brandVoice',
      width: 110,
      render: (bv) => (
        bv
          ? <Tag color="magenta" style={{ fontSize: '11px' }}>Set</Tag>
          : <Tag style={{ fontSize: '11px' }}>Not set</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      render: (date) => (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {date ? new Date(date).toLocaleDateString() : '—'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this angle?"
            description="This removes its keywords, CTAs and brand voice. This cannot be undone."
            onConfirm={() => handleDelete(record._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1 }}>
        <Sidebar />

        {/* Main Content */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>

          {/* Page Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Target size={22} style={{ color: '#ea580c' }} />
              </div>
              <div>
                <Title level={4} style={{ margin: 0 }}>Angles & CTAs</Title>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  View, add, edit or delete the Angles, Keywords, CTAs and Brand Voice used for AI answer generation.
                </Text>
              </div>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              size="large"
              style={{ backgroundColor: '#ea580c', borderColor: '#ea580c' }}
            >
              Add New Angle
            </Button>
          </div>

          {/* Info banner */}
          <div style={{
            padding: '12px 16px',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#9a3412',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Target size={16} />
            <span>
              Each Angle groups the <strong>Keywords</strong>, <strong>CTAs</strong>, <strong>Basic Info</strong> and <strong>Brand Voice</strong>
              {' '}you pick when generating AI answers. Edit or delete an angle here at any time — changes apply the next time it's selected.
            </span>
          </div>

          {/* Angles Table */}
          <Card bodyStyle={{ padding: '0' }}>
            <Table
              dataSource={angles}
              columns={columns}
              rowKey="_id"
              loading={loading}
              size="middle"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: '8px', color: '#666' }}>No angles yet.</div>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={openCreate}
                          style={{ backgroundColor: '#ea580c', borderColor: '#ea580c' }}
                        >
                          Add New Angle
                        </Button>
                      </div>
                    }
                  />
                ),
              }}
            />
          </Card>

          {/* Stats row */}
          {angles.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Total: <strong>{angles.length}</strong> angle{angles.length !== 1 ? 's' : ''}
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Angle Modal ─────────────────────────────────────────── */}
      <AngleForm
        visible={formVisible}
        onCancel={() => setFormVisible(false)}
        userId={userId}
        editingAngle={editingAngle}
        onSuccess={() => loadAngles()}
      />
    </div>
  );
};

export default AnglesPage;
